import * as React from "react";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate, fmtLocalValue, parseDateTime, parseDay } from "@/lib/intl";

const MODES = {
  date: { fmt: (v) => (v ? fmtDate(v) : ""), parse: parseDay, hint: "DD-MMM-YYYY", full: /\d{4}/ },
  "datetime-local": { fmt: fmtLocalValue, parse: parseDateTime, hint: "DD-MMM-YYYY 9:00 am", full: /\d{4}/ },
};

/**
 * Drop-in for <input type="date|datetime-local">: shows and accepts DD-MMM-YYYY whatever the browser locale,
 * keeps the same value format (YYYY-MM-DD / YYYY-MM-DDTHH:mm) and onChange(e.target.value) contract,
 * and still opens the browser's calendar from the icon.
 */
export const DateField = React.forwardRef(({
  type, value, onChange, onBlur, onKeyDown, className, min, max, name, disabled, readOnly, placeholder, ...rest
}, ref) => {
  const mode = MODES[type];
  const picker = React.useRef(null);
  const focused = React.useRef(false);
  const [text, setText] = React.useState(() => mode.fmt(value || ""));
  const [bad, setBad] = React.useState(false);
  React.useEffect(() => {
    if (!focused.current) setText(mode.fmt(value || ""));
  }, [value, mode]);
  const ok = (iso) => iso !== null && (!iso || ((!min || iso >= min) && (!max || iso <= max)));
  const emit = (iso) => {
    if (iso !== (value || "")) onChange?.({ target: { value: iso, name }, currentTarget: { value: iso, name } });
  };
  const commit = () => {
    const iso = mode.parse(text, value || "");
    setBad(!ok(iso));
    if (!ok(iso)) return;
    emit(iso);
    setText(mode.fmt(iso));
  };
  const typed = (e) => {
    setText(e.target.value);
    const iso = mode.parse(e.target.value, value || "");
    if (iso && ok(iso) && mode.full.test(e.target.value)) { setBad(false); emit(iso); }
  };
  const openPicker = () => {
    const el = picker.current;
    if (!el || disabled || readOnly) return;
    try { el.showPicker(); } catch { el.focus(); el.click(); }
  };
  return (
    <div className={cn(
      "relative flex h-9 w-full items-center gap-1 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring md:text-sm",
      bad && "border-red-400", disabled && "cursor-not-allowed opacity-50", className,
    )}>
      <input
        ref={ref} type="text" inputMode="text" autoComplete="off" name={name} value={text} disabled={disabled} readOnly={readOnly}
        placeholder={placeholder || mode.hint} aria-invalid={bad || undefined} title={bad ? `Use ${mode.hint}` : undefined}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        onFocus={() => { focused.current = true; }}
        onChange={typed}
        onBlur={(e) => { focused.current = false; commit(); onBlur?.(e); }}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); onKeyDown?.(e); }}
        {...rest}
      />
      <button type="button" tabIndex={-1} onClick={openPicker} disabled={disabled || readOnly} aria-label="Open calendar"
        className="shrink-0 text-zinc-400 transition-colors hover:text-zinc-800 disabled:pointer-events-none">
        <CalendarDays className="h-4 w-4" />
      </button>
      <input ref={picker} type={type} tabIndex={-1} aria-hidden="true" value={value || ""} min={min} max={max}
        onChange={(e) => { emit(e.target.value); setText(mode.fmt(e.target.value)); setBad(false); }}
        className="pointer-events-none absolute bottom-0 left-0 h-0 w-full opacity-0" />
    </div>
  );
});
DateField.displayName = "DateField";
